import { createFileRoute, Link } from "@tanstack/react-router";
import { PageSection } from "@/components/site/PageSection";
import { EsPageShell } from "@/components/site/EsPageShell";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";
import { faqPageJson } from "@/lib/faq-schema";
import { PHYSICIAN_ID_REF } from "@/lib/medical-schema";
import { esStoryHeadline } from "@/lib/story-titles-es";
import { esStoryCopy, esStories } from "@/lib/es-stories";
import {
  stories,
  storySlug,
  storyCredit,
  storyImageObject,
  largestUrl,
} from "@/lib/results-stories";

const ES_URL = `${SITE}/es/antes-y-despues`;

const ES_FAQS: { question: string; answer: string }[] = [
  {
    question: "¿Cuánto peso puedo esperar perder después de la cirugía bariátrica?",
    answer:
      "La mayoría de los pacientes de manga gástrica del Dr. Fitzer pierden alrededor del 70% del exceso de peso, y los datos de Virginia Bariatric muestran que los pacientes se mantienen por encima del 70% de pérdida del exceso de peso desde el primer año hasta el noveno. Los pacientes de bypass gástrico suelen perder una cantidad similar o un poco mayor. Los resultados varían según el peso inicial, las condiciones médicas, la actividad y los hábitos de alimentación.",
  },
  {
    question: "¿Cuánto tiempo toma ver los resultados de antes y después?",
    answer:
      "La pérdida de peso es más rápida en los primeros tres a seis meses después de la cirugía y por lo general continúa de 12 a 18 meses. La mayoría de las fotos de esta página se tomaron entre uno y dos años después de la operación.",
  },
  {
    question: "¿Estos son pacientes reales del Dr. Fitzer?",
    answer:
      "Sí. Cada foto de antes y después y cada testimonio de esta página provienen de un paciente real de Virginia Bariatric Surgery que autorizó compartir su historia. Las fotos no son de banco de imágenes ni fueron alteradas digitalmente.",
  },
  {
    question: "¿Me quedará piel flácida después de bajar de peso?",
    answer:
      "Algunos pacientes tienen piel flácida después de una pérdida de peso importante, y la cantidad depende de la edad, la genética, el peso inicial y cuánto tiempo se cargó el peso extra. El entrenamiento de fuerza y una pérdida de peso constante y bien nutrida ayudan. La cirugía de contorno corporal es una opción para los pacientes a quienes les molesta, una vez que su peso está estable.",
  },
  {
    question: "¿Podré mantener el peso a largo plazo?",
    answer:
      "Sí, con seguimiento. Los pacientes que se mantienen en contacto con nuestro equipo, cuidan su consumo de proteína, se mantienen activos y asisten a los grupos de apoyo conservan mejor sus resultados. Si el peso comienza a regresar, podemos ayudar con un plan estructurado y, cuando es apropiado, con medicamentos para bajar de peso.",
  },
  {
    question: "¿Cómo empiezo con la cirugía de pérdida de peso en el norte de Virginia?",
    answer:
      "Pida una cita con el Dr. Fitzer en Virginia Bariatric Surgery. Revisamos su historia médica y su cobertura de seguro, conversamos si le conviene más la manga gástrica, el bypass gástrico, el balón o la pérdida de peso con medicamentos, y le explicamos los pasos hasta la cirugía.",
  },
];

function abs(url: string) {
  return url.startsWith("http") ? url : `${SITE}${url}`;
}

/**
 * Spanish ImageObject: keeps creator (required by Google Images metadata)
 * and drops only the English description, which the caller replaces.
 */
function esStoryImageObject(options: Parameters<typeof storyImageObject>[0]) {
  const { description: _description, ...image } = storyImageObject(options);
  return image;
}

/** Spanish ImageObject nodes for every photo rendered on the page. */
function esImageNodes() {
  const nodes: Record<string, unknown>[] = [];
  stories.forEach((story, index) => {
    const copy = esStoryCopy(story, index);
    const slug = copy.slug;
    const common = { story, isPartOf: `${ES_URL}#galeria` };
    if (story.single) {
      nodes.push({
        ...esStoryImageObject({
          id: `${ES_URL}#imagen-${slug}`,
          img: story.before,
          name: copy.headline,
          caption: copy.caption,
          ...common,
        }),
        description: copy.caption,
        inLanguage: "es-US",
      });
      return;
    }
    nodes.push(
      {
        ...esStoryImageObject({
          id: `${ES_URL}#imagen-${slug}-antes`,
          img: story.before,
          name: `${copy.label} — antes de la cirugía de pérdida de peso`,
          caption: `${copy.label} antes de la cirugía de pérdida de peso con el Dr. Matthew Fitzer.`,
          ...common,
        }),
        description: copy.caption,
        inLanguage: "es-US",
      },
      {
        ...esStoryImageObject({
          id: `${ES_URL}#imagen-${slug}-despues`,
          img: story.after,
          name: `${copy.label} — después de la cirugía de pérdida de peso`,
          caption: `${copy.label} después de la cirugía de pérdida de peso con el Dr. Matthew Fitzer.`,
          ...common,
        }),
        description: copy.caption,
        inLanguage: "es-US",
      },
    );
  });
  return nodes;
}

/**
 * Emitted as three separate JSON-LD blocks rather than one @graph. The combined
 * graph was ~105 KB, by far the largest block on the site and the only one that
 * exceeds the size audit crawlers reliably parse; splitting it keeps every node
 * and relationship intact while leaving each block comfortably small.
 */
function buildEsResultsSchema() {
  const images = esImageNodes();
  return [
      {
        ...esWebPageSchema("stories"),
        specialty: "https://schema.org/Surgical",
        reviewedBy: PHYSICIAN_ID_REF,
        primaryImageOfPage: { "@id": String(images[0]!["@id"]) },
        mainEntity: { "@id": `${ES_URL}#historias` },
        hasPart: [{ "@id": `${ES_URL}#galeria` }, { "@id": `${ES_URL}#historias` }],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "@id": `${ES_URL}#historias`,
        name: "Historias de antes y después de nuestros pacientes",
        description:
          "Historias individuales de pacientes de cirugía de pérdida de peso, cada una con fotos de antes y después.",
        numberOfItems: stories.length,
        itemListOrder: "https://schema.org/ItemListOrderAscending",
        // Summary-page form, matching the English /results ItemList: each
        // entry points at the story page with its name and photo. Nesting a
        // full CreativeWork per entry made validators evaluate the list as a
        // carousel, and the story pages are noindex anyway.
        itemListElement: stories.map((story, index) => {
          const copy = esStoryCopy(story, index);
          return {
            "@type": "ListItem",
            position: index + 1,
            url: `${SITE}/es/antes-y-despues/historia/${copy.slug}`,
            name: copy.headline,
            image: abs(largestUrl(story.single ? story.before : story.after)),
          };
        }),
      },
      {
        "@context": "https://schema.org",
        "@type": "ImageGallery",
        "@id": `${ES_URL}#galeria`,
        name: "Galería de antes y después de cirugía bariátrica",
        description:
          "Fotografías de antes y después de pacientes de pérdida de peso del Dr. Matthew Fitzer en Virginia Bariatric Surgery.",
        inLanguage: "es-US",
        isPartOf: { "@id": `${ES_URL}#webpage` },
        image: images,
      },
  ];
}

export const Route = createFileRoute("/es/antes-y-despues")({
  head: () => {
    const base = esHead("stories");
    return {
      ...base,
      links: [
        ...base.links,
        {
          rel: "preload",
          as: "image",
          href: stories[0]!.before.url,
          imagesrcset: stories[0]!.before.srcSet,
          imagesizes: "(min-width: 768px) 33rem, calc(100vw - 3rem)",
          fetchPriority: "high",
        },
      ],
      scripts: import.meta.env.SSR
        ? [
            {
              type: "application/ld+json",
              children: faqPageJson({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                inLanguage: "es-US",
                name: "Antes y después — preguntas frecuentes",
                mainEntity: ES_FAQS.map((faq) => ({
                  "@type": "Question",
                  name: faq.question,
                  acceptedAnswer: { "@type": "Answer", text: faq.answer },
                })),
              }),
            },
            ...buildEsResultsSchema().map((node) => ({
              type: "application/ld+json",
              children: JSON.stringify(node),
            })),
          ]
        : [],
    };
  },
  component: EsBeforeAfterPage,
});

function EsBeforeAfterPage() {
  return (
    <EsPageShell
      page="stories"
      title="¡Estamos orgullosos de cada historia de éxito!"
      intro="Estas fotografías son de pacientes reales del Dr. Matthew Fitzer en Herndon, Virginia, quienes autorizaron compartir sus imágenes. Los resultados varían de una persona a otra."
    >
      <PageSection className="py-4" maxWidth="4xl">
        <p className="text-center text-foreground/85">
          La mayoría de estos pacientes se operaron de{" "}
          <Link to="/es/manga-gastrica" className="font-medium text-brand-logo hover:underline">
            manga gástrica
          </Link>{" "}
          o de{" "}
          <Link to="/es/bypass-gastrico" className="font-medium text-brand-logo hover:underline">
            bypass gástrico
          </Link>
          . Para saber si usted califica, llame al{" "}
          <a
            href="tel:7037099771"
            aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771"
            className="font-medium text-brand-logo"
          >
            (703) 709-9771
          </a>{" "}
          o{" "}
          <Link to="/es/contacto" className="font-medium text-brand-logo hover:underline">
            escríbanos
          </Link>
          .
        </p>
      </PageSection>

      <section aria-label="Galería de antes y después" className="bg-brand-soft/40 py-6">
        <div className="mx-auto max-w-[33.408rem] space-y-4 px-6">
          {stories.map((s, i) => {
            const slug = storySlug(s, i);
            const headline = esStoryHeadline(slug, s.label);
            const copy = esStoryCopy(s, i);
            return (
              <article
                key={slug}
                id={slug}
                className="defer-render overflow-hidden rounded-2xl border border-border bg-card shadow-sm"
              >
                <figure className="m-0">
                  {s.single ? (
                    <img
                      src={s.before.url}
                      srcSet={s.before.srcSet}
                      sizes={s.before.sizes}
                      width={s.before.width}
                      height={s.before.height}
                      alt={`${headline} — paciente de cirugía de pérdida de peso en Virginia Bariatric Surgery`}
                      loading={i === 0 ? "eager" : "lazy"}
                      {...(i === 0
                        ? { fetchpriority: "high" as const }
                        : { decoding: "async" as const })}
                      className={`${s.aspectClass ?? ""} block w-full ${s.aspectClass ? "object-cover" : "h-auto"}`}
                    />
                  ) : (
                    <div className="grid grid-cols-2">
                      <img
                        src={s.before.url}
                        srcSet={s.before.srcSet}
                        sizes={s.before.sizes}
                        width={s.before.width}
                        height={s.before.height}
                        alt={`${copy.label} antes de la cirugía de pérdida de peso con el Dr. Matthew Fitzer`}
                        loading={i === 0 ? "eager" : "lazy"}
                        {...(i === 0
                          ? { fetchpriority: "high" as const }
                          : { decoding: "async" as const })}
                        style={{ objectPosition: s.beforePosition ?? "center top" }}
                        className={`${s.aspectClass ?? "aspect-[4/3]"} w-full object-cover`}
                      />
                      <img
                        src={s.after.url}
                        srcSet={s.after.srcSet}
                        sizes={s.after.sizes}
                        width={s.after.width}
                        height={s.after.height}
                        alt={`${copy.label} después de la cirugía de pérdida de peso con el Dr. Matthew Fitzer`}
                        loading={i === 0 ? "eager" : "lazy"}
                        {...(i === 0
                          ? { fetchpriority: "high" as const }
                          : { decoding: "async" as const })}
                        style={{ objectPosition: s.afterPosition ?? "center top" }}
                        className={`${s.aspectClass ?? "aspect-[4/3]"} w-full object-cover`}
                      />
                    </div>
                  )}
                  <figcaption className="px-4 pt-2 text-sm text-muted-foreground">
                    {copy.caption}
                    <small className="mt-1 block text-xs text-muted-foreground/80">
                      Crédito: {storyCredit(s).credit}. Foto de paciente compartida con
                      permiso; sin retocar.
                    </small>
                  </figcaption>
                </figure>
                <div className="border-t border-border px-4 py-3">
                  <p className="font-serif text-2xl">{headline}</p>
                  {copy.tagline && (
                    <p className="mt-1 text-sm text-muted-foreground">{copy.tagline}</p>
                  )}
                  {copy.quote && (
                    <p className="mt-1 leading-[1.6] text-foreground/80">{copy.quote}</p>
                  )}
                  <Link
                    to="/es/antes-y-despues/historia/$slug"
                    params={{ slug }}
                    className="mt-2 inline-block text-sm font-medium text-brand-logo hover:underline"
                  >
                    Ver la historia completa &rarr;
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <nav className="mx-auto max-w-4xl px-6 pt-8 pb-2" aria-labelledby="todas-las-historias">
        <h2
          id="todas-las-historias"
          className="text-center font-serif text-2xl text-brand-logo md:text-3xl"
        >
          Todas las historias de antes y después
        </h2>
        <ul className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {esStories.map((copy) => (
            <li key={`todas-${copy.slug}`}>
              <Link
                to="/es/antes-y-despues/historia/$slug"
                params={{ slug: copy.slug }}
                className="block rounded-lg border border-border px-3 py-2 text-sm hover:border-brand-logo hover:text-brand-logo"
              >
                {copy.label}
                {copy.tagline ? (
                  <span className="block text-xs text-muted-foreground">{copy.tagline}</span>
                ) : null}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      <section
        id="preguntas"
        className="mx-auto max-w-3xl px-6 pt-6 pb-2"
        aria-labelledby="preguntas-resultados"
      >
        <h2
          id="preguntas-resultados"
          className="text-center font-serif text-2xl text-brand-logo md:text-3xl"
        >
          Preguntas frecuentes sobre los resultados
        </h2>
        <dl className="mt-5 space-y-5">
          {ES_FAQS.map((faq, i) => (
            <div key={faq.question} id={`pregunta-${i + 1}`} className="rounded-xl border border-border p-5">
              <dt className="text-lg font-medium">{faq.question}</dt>
              <dd className="mt-2 leading-[1.6] text-foreground/80">{faq.answer}</dd>
            </div>
          ))}
        </dl>
      </section>
    </EsPageShell>
  );
}
